import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import { exists } from "helpers/booleans";

export interface ProgressBarProps {
    currentValue: number;
    maxValue: number;
    hideCount?: boolean;
    variant?:
        | undefined
        | "cloud-assets"
        | "thick"
        | "video-storage-medium"
        | "video-storage-max"
        | "thin-video-storage"
        | "thin-video-storage-medium"
        | "thin-video-storage-max";
    /** Bar completion displays the maxValue plus 1. Effectively, bar never displays full, even at X/X. */
    isStepped?: boolean;
    /** Bar shows what is left (max - current) instead of what is used. Count text follows. */
    showRemaining?: boolean;
    /** Optional suffix for the count text, e.g. "min". */
    unit?: string;
    /** Keeps the "a / b unit" count with plain digits instead of the compact "a/b unit". */
    plainCount?: boolean;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
    currentValue,
    maxValue,
    hideCount,
    variant,
    isStepped = false,
    showRemaining = false,
    unit,
    plainCount = false
}: ProgressBarProps) => {
    const { i18n } = useTranslation();
    const _maxValue = isStepped ? maxValue + 1 : maxValue;
    const steppedValue = isStepped ? currentValue + 1 : currentValue;
    const _currentvalue = showRemaining
        ? Math.max((maxValue ?? 0) - (currentValue ?? 0), 0)
        : steppedValue;

    const percentage = useMemo(() => {
        if (_currentvalue === undefined || _maxValue === undefined) return 0;

        return _currentvalue <= _maxValue
            ? (_currentvalue / _maxValue) * 100
            : 100;
    }, [_currentvalue, _maxValue]);

    const countText = useMemo(() => {
        if (unit && !plainCount) {
            const formatter = new Intl.NumberFormat(i18n.language, {
                useGrouping: "always"
            });
            return `${formatter.format(_currentvalue || 0)}/${formatter.format(maxValue || 0)} ${unit}`;
        }
        return `${_currentvalue || 0} / ${maxValue || 0}${unit ? ` ${unit}` : ""}`;
    }, [_currentvalue, maxValue, unit, plainCount, i18n.language]);

    return (
        <div
            className={`${styles["progress-container"]} ${
                exists(variant) ? styles[variant] : ""
            }`}
        >
            <div className={styles["progress-bar"]}>
                <div className={`${styles["progress-background"]}`} />
                <div
                    className={`${styles["progress-foreground"]}`}
                    style={{ width: percentage + "%" }}
                />
            </div>
            {!hideCount && !!_maxValue && (
                <div className={styles["progress-text"]}>{countText}</div>
            )}
        </div>
    );
};
